import { useEffect, useState } from 'react';
import { Edit, Plus, Trash, X } from 'lucide-react';
import { toast } from 'react-toastify';
import type { Configuracion, TarifaNombrada, PricingItem } from '../../types/configuraciones';
import type { BorradoTarifaResponse, ReprecioTarifaResponse, TarifaNombradaInput } from '../../services/api/configurationService';
import type { RecalculoDesdePlazas } from '../../services/api/suscripcionService';
import {
    useNivelesQuery,
    useCrearTarifaNombradaMutation,
    useActualizarTarifaNombradaMutation,
    useEliminarTarifaNombradaMutation,
    useRecalcularDesdePlazasMutation,
} from '../../services/hooks';
import CombinacionesTarifas from './CombinacionesTarifas';
import { textoLineaDesglose } from '../suscripciones/PrecioSuscripcion';

const FRECUENCIAS = [1, 2, 3, 4, 5, 6];

/**
 * Tarifas con nombre de un centro (ver pricingService en el back). Cada tarifa
 * cubre unos niveles; las plazas del cliente se agrupan por tarifa y se cobra
 * cada grupo aparte. Los niveles que no estén en ninguna van por la tarifa
 * general, así que un centro que no cree ninguna no nota nada.
 */
const TarifasNombradasSection = ({ config }: { config: Configuracion }) => {
    const { data: todosLosNiveles = [] } = useNivelesQuery();
    const niveles = todosLosNiveles
        .filter((n): n is typeof n & { _id: string } => !!n._id)
        .map((n) => ({ _id: n._id, nombre: n.nombre }));
    const [editando, setEditando] = useState<TarifaNombrada | 'nueva' | null>(null);
    const [aBorrar, setABorrar] = useState<TarifaNombrada | null>(null);

    const tarifas = config.tarifasNombradas || [];
    const nombreNivel = (id: string) => niveles.find((n) => n._id === id)?.nombre || 'Nivel eliminado';

    // Niveles con más de una tarifa: al inscribir se pregunta cuál aplica.
    const nivelesConVarias = niveles.filter((n) => tarifas.filter((t) => t.niveles.includes(n._id)).length > 1);
    // Niveles que no están en ninguna: se cobran con la general SIN avisar. Si
    // el centro usa tarifas por nivel, casi siempre es un olvido (nivel nuevo).
    const nivelesSinTarifa = tarifas.length ? niveles.filter((n) => !tarifas.some((t) => t.niveles.includes(n._id))) : [];
    const [aplicando, setAplicando] = useState(false);

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex justify-between items-start gap-4 mb-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">Tarifas por nivel</h3>
                    <p className="text-sm text-gray-500 max-w-2xl">
                        Para centros que cobran distinto según el nivel. Si un cliente va a niveles con tarifas
                        distintas, se cobra cada tarifa por sus clases y se suma (1 de suelo + 1 de máquina = precio
                        de 1x suelo + precio de 1x máquina), salvo que haya un precio combinado para esa mezcla (abajo).
                        Los niveles que no estén en ninguna usan la tarifa general.
                    </p>
                </div>
                <button
                    onClick={() => setEditando('nueva')}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded shrink-0"
                >
                    <Plus size={16} /> Nueva tarifa
                </button>
            </div>

            {tarifas.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                    Sin tarifas por nivel: todos los niveles usan la tarifa general.
                </p>
            ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {tarifas.map((t) => (
                        <div key={t._id} className="border border-gray-300 rounded p-3">
                            <div className="flex justify-between items-start gap-2">
                                <div>
                                    <div className="font-semibold text-gray-800">{t.nombre}</div>
                                    <div className="text-xs text-gray-500">{t.niveles.map(nombreNivel).join(', ')}</div>
                                    {t.cerradaAltas && (
                                        <span className="inline-block mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5">
                                            Cerrada a nuevas altas
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setEditando(t)} className="p-1 text-gray-500 hover:text-gray-800" title="Modificar">
                                        <Edit size={16} />
                                    </button>
                                    <button onClick={() => setABorrar(t)} className="p-1 text-gray-500 hover:text-red-600" title="Eliminar">
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {t.precios.map((p) => (
                                    <span key={p.weeklySessions} className="text-sm border border-gray-200 rounded px-2 py-0.5">
                                        {p.weeklySessions}x · <strong>{p.price} €</strong>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {nivelesConVarias.length > 0 && (
                <p className="mt-3 text-xs text-gray-500">
                    {nivelesConVarias.map((n) => n.nombre).join(', ')} {nivelesConVarias.length === 1 ? 'tiene' : 'tienen'} varias
                    tarifas: al inscribir o dar una plaza se pregunta cuál aplica a ese cliente.
                </p>
            )}

            {nivelesSinTarifa.length > 0 && (
                <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <strong>{nivelesSinTarifa.map((n) => n.nombre).join(', ')}</strong>{' '}
                    {nivelesSinTarifa.length === 1 ? 'no está' : 'no están'} en ninguna tarifa por nivel: se
                    {nivelesSinTarifa.length === 1 ? ' cobra' : ' cobran'} con la tarifa general. Si no es lo que quieres,
                    añade {nivelesSinTarifa.length === 1 ? 'ese nivel' : 'esos niveles'} a una tarifa.
                </p>
            )}

            {/* Combinar tiene sentido a partir de dos tarifas */}
            {tarifas.length >= 2 && <CombinacionesTarifas config={config} tarifas={tarifas} />}

            {tarifas.length > 0 && (
                <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-600 max-w-2xl">
                        Crear o modificar tarifas no cambia el importe de los clientes que ya existen. Para aplicarlas,
                        recalcula sus importes desde sus plazas (verás la lista antes de confirmar).
                    </p>
                    <button
                        onClick={() => setAplicando(true)}
                        className="px-4 py-2 border border-gray-300 rounded text-gray-800 bg-white hover:bg-gray-50 shrink-0"
                    >
                        Aplicar tarifas a los clientes actuales
                    </button>
                </div>
            )}

            {aplicando && <AplicarTarifasModal onClose={() => setAplicando(false)} />}

            {editando && (
                <TarifaModal
                    config={config}
                    tarifa={editando === 'nueva' ? null : editando}
                    niveles={niveles}
                    onClose={() => setEditando(null)}
                />
            )}

            {aBorrar && <BorrarTarifaModal config={config} tarifa={aBorrar} onClose={() => setABorrar(null)} />}
        </div>
    );
};

const TarifaModal = ({
    config, tarifa, niveles, onClose,
}: {
    config: Configuracion;
    tarifa: TarifaNombrada | null;
    niveles: Array<{ _id: string; nombre: string }>;
    onClose: () => void;
}) => {
    const [nombre, setNombre] = useState(tarifa?.nombre || '');
    const [nivelesSel, setNivelesSel] = useState<string[]>(tarifa?.niveles || []);
    // Precio por frecuencia; vacío = esa frecuencia no se ofrece en esta tarifa.
    const [precios, setPrecios] = useState<Record<number, string>>(
        Object.fromEntries((tarifa?.precios || []).map((p) => [p.weeklySessions, String(p.price)]))
    );
    const [cerradaAltas, setCerradaAltas] = useState(!!tarifa?.cerradaAltas);
    const [preview, setPreview] = useState<ReprecioTarifaResponse | null>(null);
    const crear = useCrearTarifaNombradaMutation();
    const actualizar = useActualizarTarifaNombradaMutation();
    const ocupado = crear.isPending || actualizar.isPending;

    const datos = (): TarifaNombradaInput | null => {
        if (!nombre.trim()) { toast.warning('Ponle un nombre a la tarifa'); return null; }
        if (!nivelesSel.length) { toast.warning('Elige al menos un nivel'); return null; }
        const lista: PricingItem[] = FRECUENCIAS
            .filter((f) => precios[f] !== undefined && precios[f] !== '')
            .map((f) => ({ weeklySessions: f, price: Number(precios[f]) }));
        if (!lista.length) { toast.warning('Pon al menos un precio'); return null; }
        if (lista.some((p) => !Number.isFinite(p.price) || p.price < 0)) { toast.warning('Revisa los precios'); return null; }
        return { nombre: nombre.trim(), niveles: nivelesSel, precios: lista, cerradaAltas };
    };

    const guardar = async () => {
        const d = datos();
        if (!d) return;
        try {
            if (!tarifa) {
                await crear.mutateAsync({ configId: config._id, data: d });
                onClose();
                return;
            }
            // Editar: primero se enseña a quién le cambia el importe.
            if (!preview) {
                const r = await actualizar.mutateAsync({ configId: config._id, tarifaId: tarifa._id, data: { ...d, dryRun: true } });
                if (r.actualizadas === 0 && !r.errores?.length) {
                    await actualizar.mutateAsync({ configId: config._id, tarifaId: tarifa._id, data: d });
                    toast.success('Tarifa actualizada');
                    onClose();
                    return;
                }
                setPreview(r);
                return;
            }
            const r = await actualizar.mutateAsync({ configId: config._id, tarifaId: tarifa._id, data: d });
            toast.success(`Tarifa actualizada. ${r.actualizadas} cliente${r.actualizadas !== 1 ? 's' : ''} con importe nuevo.`);
            onClose();
        } catch { /* el toast lo pone el hook */ }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">{tarifa ? 'Modificar tarifa' : 'Nueva tarifa'}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>

                {preview ? (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            Al guardar cambiará el importe de <strong>{preview.actualizadas}</strong> cliente{preview.actualizadas !== 1 ? 's' : ''}
                            {preview.omitidasPrecioFijo > 0 && <> (no se tocan {preview.omitidasPrecioFijo} con precio fijo)</>}.
                        </p>
                        {preview.cambios.length > 0 && (
                            <div className="border rounded max-h-60 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr><th className="text-left px-2 py-1">Cliente</th><th className="text-right px-2 py-1">Antes</th><th className="text-right px-2 py-1">Después</th></tr>
                                    </thead>
                                    <tbody>
                                        {preview.cambios.map((c) => (
                                            <tr key={c.id} className="border-t">
                                                <td className="px-2 py-1">{c.cliente}</td>
                                                <td className="px-2 py-1 text-right">{c.antes} €</td>
                                                <td className="px-2 py-1 text-right font-medium">{c.despues} €</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {preview.errores?.length ? (
                            <div className="text-sm text-red-600">
                                {preview.errores.length} cliente{preview.errores.length !== 1 ? 's' : ''} no se pueden recalcular y se quedan como están:
                                <ul className="list-disc pl-5">
                                    {preview.errores.map((e) => <li key={e.id}>{e.cliente || e.id}: {e.motivo}</li>)}
                                </ul>
                            </div>
                        ) : null}
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setPreview(null)} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Volver
                            </button>
                            <button onClick={guardar} disabled={ocupado} className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-50">
                                Confirmar y actualizar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nombre</label>
                            <input
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                placeholder="Ej. Máquina socio fundador"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Niveles que cubre</label>
                            <div className="flex flex-wrap gap-2">
                                {niveles.map((n) => {
                                    const activo = nivelesSel.includes(n._id);
                                    return (
                                        <button
                                            type="button"
                                            key={n._id}
                                            onClick={() => setNivelesSel((prev) => activo ? prev.filter((x) => x !== n._id) : [...prev, n._id])}
                                            className={`px-3 py-1 rounded-full text-sm border ${activo ? 'bg-brand-primary text-white border-transparent' : 'bg-white text-gray-700 border-gray-300'}`}
                                        >
                                            {n.nombre}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Varios niveles en la misma tarifa cuentan juntos (1 de cada uno = precio de 2x).
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Precio por clases a la semana</label>
                            <div className="grid grid-cols-3 gap-2">
                                {FRECUENCIAS.map((f) => (
                                    <label key={f} className="text-sm text-gray-600">
                                        {f}x semana
                                        <div className="flex items-center">
                                            <input
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                value={precios[f] ?? ''}
                                                onChange={(e) => setPrecios((prev) => ({ ...prev, [f]: e.target.value }))}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                            />
                                            <span className="ml-1">€</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Deja vacío lo que no se ofrezca.</p>
                        </div>

                        <label className="flex items-start gap-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={cerradaAltas}
                                onChange={(e) => setCerradaAltas(e.target.checked)}
                                className="mt-0.5"
                            />
                            <span>
                                Cerrada a nuevas altas
                                <span className="block text-xs text-gray-500">
                                    Para tarifas que ya no se ofrecen (p. ej. socio fundador pasada la fecha). Quien la tiene la
                                    conserva. Un operador ya no puede asignarla; un admin sí, confirmándolo.
                                </span>
                            </span>
                        </label>

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Cancelar
                            </button>
                            <button onClick={guardar} disabled={ocupado} className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-50">
                                {tarifa ? 'Guardar' : 'Crear tarifa'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Borrar una tarifa, también con clientes: es la vía para deshacer. Sus
 * clientes pasan a la otra tarifa de su nivel, o a la general si no queda
 * ninguna; si quedan varias, se elige el destino aquí. Primero vista previa.
 */
const BorrarTarifaModal = ({ config, tarifa, onClose }: { config: Configuracion; tarifa: TarifaNombrada; onClose: () => void }) => {
    const eliminar = useEliminarTarifaNombradaMutation();
    const [destino, setDestino] = useState<string>('');
    const [preview, setPreview] = useState<BorradoTarifaResponse | null>(null);

    useEffect(() => {
        setPreview(null);
        eliminar.mutateAsync({ configId: config._id, tarifaId: tarifa._id, dryRun: true, destino: destino || null })
            .then(setPreview)
            .catch(() => onClose());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destino]);

    const confirmar = async () => {
        try {
            await eliminar.mutateAsync({ configId: config._id, tarifaId: tarifa._id, dryRun: false, destino: destino || null });
            toast.success(`Tarifa "${tarifa.nombre}" eliminada`);
            onClose();
        } catch { /* el toast lo pone el hook */ }
    };

    const omitidas = preview ? preview.omitidas.precioFijo + preview.omitidas.ilimitada + preview.omitidas.sinPlazas : 0;
    // Destino solo hace falta si al quitarla quedan varias tarifas para su nivel.
    const necesitaDestino = !!preview && preview.faltaElegir.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Eliminar tarifa "{tarifa.nombre}"</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>

                {!preview ? (
                    <p className="text-sm text-gray-500">Calculando…</p>
                ) : (
                    <div className="space-y-4 text-sm">
                        {preview.clientes === 0 ? (
                            <p className="text-gray-700">Ningún cliente activo tiene esta tarifa. Se puede eliminar sin más.</p>
                        ) : (
                            <p className="text-gray-700">
                                La tienen <strong>{preview.clientes}</strong> cliente{preview.clientes !== 1 ? 's' : ''}.
                                Pasarán a {preview.opcionesDestino.length
                                    ? (destino ? `"${preview.opcionesDestino.find((o) => o._id === destino)?.nombre}"` : 'la otra tarifa de su nivel')
                                    : 'la tarifa general'}.{' '}
                                {preview.cambios.length} cambian de importe
                                {preview.sinCambios > 0 && <>, {preview.sinCambios} se quedan igual</>}
                                {omitidas > 0 && <> y {omitidas} no cambian de importe (precio fijo, ilimitada o sin plazas)</>}.
                            </p>
                        )}

                        {preview.combinacionesBorradas?.length ? (
                            <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                También se borra{preview.combinacionesBorradas.length !== 1 ? 'n' : ''} la{preview.combinacionesBorradas.length !== 1 ? 's' : ''} combinaci{preview.combinacionesBorradas.length !== 1 ? 'ones' : 'ón'}{' '}
                                <strong>{preview.combinacionesBorradas.map((c) => `"${c.nombre}"`).join(', ')}</strong>, que usa{preview.combinacionesBorradas.length !== 1 ? 'n' : ''} esta
                                tarifa. Sus clientes pasan a la combinación de su tarifa nueva si existe, o a la suma.
                            </p>
                        ) : null}

                        {(necesitaDestino || destino) && preview.opcionesDestino.length > 1 && (
                            <div>
                                <label className="block font-medium text-gray-700">¿A qué tarifa pasan sus clientes?</label>
                                <select
                                    value={destino}
                                    onChange={(e) => setDestino(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                >
                                    <option value="">Elige la tarifa…</option>
                                    {preview.opcionesDestino.map((o) => <option key={o._id} value={o._id}>{o.nombre}</option>)}
                                </select>
                            </div>
                        )}

                        {preview.cambios.length > 0 && (
                            <div className="border rounded max-h-60 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                                        <tr>
                                            <th className="text-left px-2 py-1">Cliente</th>
                                            <th className="text-right px-2 py-1">Antes</th>
                                            <th className="text-right px-2 py-1">Después</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.cambios.map((c) => (
                                            <tr key={c.id} className="border-t">
                                                <td className="px-2 py-1">{c.cliente}</td>
                                                <td className="px-2 py-1 text-right">{c.antes} €</td>
                                                <td className="px-2 py-1 text-right font-medium">{c.despues} €</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {preview.sinPrecio.length > 0 && (
                            <div className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                                No se puede eliminar: estos clientes se quedarían sin precio para su número de clases.
                                Añade ese precio en la tarifa de destino o en la general.
                                <ul className="list-disc pl-5 mt-1">
                                    {preview.sinPrecio.map((s) => <li key={s.id}>{s.cliente}: {s.motivo}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Cancelar
                            </button>
                            <button
                                onClick={confirmar}
                                disabled={eliminar.isPending || preview.bloqueado}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                            >
                                Eliminar tarifa
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Recalcula el importe de todos los clientes del centro desde sus plazas, con
 * las tarifas vigentes. Primero vista previa; nada se guarda hasta confirmar.
 */
const AplicarTarifasModal = ({ onClose }: { onClose: () => void }) => {
    const recalcular = useRecalcularDesdePlazasMutation();
    const [preview, setPreview] = useState<RecalculoDesdePlazas | null>(null);

    useEffect(() => {
        recalcular.mutateAsync({ dryRun: true }).then(setPreview).catch(() => onClose());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmar = async () => {
        try {
            const r = await recalcular.mutateAsync({ dryRun: false });
            toast.success(`${r.actualizadas} cliente${r.actualizadas !== 1 ? 's' : ''} con importe actualizado`);
            onClose();
        } catch { /* el toast lo pone el hook */ }
    };

    const omitidas = preview ? preview.omitidas.precioFijo + preview.omitidas.ilimitada + preview.omitidas.sinPlazas : 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Aplicar tarifas a los clientes actuales</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>

                {!preview ? (
                    <p className="text-sm text-gray-500">Calculando…</p>
                ) : (
                    <div className="space-y-4 text-sm">
                        <p className="text-gray-700">
                            De <strong>{preview.total}</strong> clientes activos: <strong>{preview.cambios.length}</strong> cambian de
                            importe, {preview.sinCambios} se quedan igual
                            {omitidas > 0 && (
                                <> y {omitidas} no se tocan
                                    ({[
                                        preview.omitidas.precioFijo && `${preview.omitidas.precioFijo} con precio fijo`,
                                        preview.omitidas.ilimitada && `${preview.omitidas.ilimitada} ilimitada${preview.omitidas.ilimitada !== 1 ? 's' : ''}`,
                                        preview.omitidas.sinPlazas && `${preview.omitidas.sinPlazas} sin plazas`,
                                    ].filter(Boolean).join(', ')})</>
                            )}.
                        </p>

                        {preview.cambios.length > 0 && (
                            <div className="border rounded max-h-72 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 text-gray-600 sticky top-0">
                                        <tr>
                                            <th className="text-left px-2 py-1">Cliente</th>
                                            <th className="text-left px-2 py-1">Desglose nuevo</th>
                                            <th className="text-right px-2 py-1">Antes</th>
                                            <th className="text-right px-2 py-1">Después</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.cambios.map((c) => (
                                            <tr key={c.id} className="border-t align-top">
                                                <td className="px-2 py-1">{c.cliente}</td>
                                                <td className="px-2 py-1 text-gray-600">
                                                    {c.desglose.map((l) => `${textoLineaDesglose(l)} · ${l.importe} €`).join(' + ')}
                                                </td>
                                                <td className="px-2 py-1 text-right">{c.antes} €</td>
                                                <td className="px-2 py-1 text-right font-medium">{c.despues} €</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {preview.faltaElegir.length > 0 && (
                            <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                <strong>{preview.faltaElegir.length}</strong> cliente{preview.faltaElegir.length !== 1 ? 's' : ''} necesita{preview.faltaElegir.length !== 1 ? 'n' : ''} que
                                se elija su tarifa en su ficha (Editar suscripción). Se quedan como están:
                                <ul className="list-disc pl-5 mt-1">
                                    {preview.faltaElegir.map((f) => <li key={f.id}>{f.cliente}: {f.opciones.join(' o ')}</li>)}
                                </ul>
                            </div>
                        )}

                        {preview.sinPrecio.length > 0 && (
                            <div className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                                Sin precio para su número de clases (se quedan como están):
                                <ul className="list-disc pl-5 mt-1">
                                    {preview.sinPrecio.map((f) => <li key={f.id}>{f.cliente}: {f.motivo}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Cancelar
                            </button>
                            {preview.cambios.length > 0 && (
                                <button
                                    onClick={confirmar}
                                    disabled={recalcular.isPending}
                                    className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-50"
                                >
                                    Actualizar {preview.cambios.length} importe{preview.cambios.length !== 1 ? 's' : ''}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TarifasNombradasSection;
